/* eslint-disable react/prop-types */
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import AuthShowcase from "@/components/auth/AuthShowcase";
import { ArrowRight, Lock, Eye, EyeSlash, CheckCircle, XCircle } from "@phosphor-icons/react";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [tokenError, setTokenError] = useState(null);
  const [touched, setTouched] = useState({ password: false, confirm: false });

  // Inline validation — mirrors the backend rule (8+ chars).
  const passwordIssue = password.length === 0
    ? null
    : password.length < 8
      ? "Mínimo de 8 caracteres."
      : null;
  const confirmIssue = confirm.length === 0
    ? null
    : confirm !== password
      ? "As senhas não coincidem."
      : null;

  useEffect(() => {
    if (!token) setTokenError("Link de recuperação não encontrado.");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setTouched({ password: true, confirm: true });
    if (password.length < 8) { toast.error("Senha precisa ter pelo menos 8 caracteres."); return; }
    if (password !== confirm) { toast.error("As senhas não coincidem."); return; }
    setLoading(true);
    try {
      await axios.post(`${API_BASE}/auth/reset-password`, {
        token, new_password: password,
      });
      setDone(true);
      setTimeout(() => nav("/login"), 3000);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Não consegui processar o link.";
      setTokenError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen bg-white text-slate-900 flex overflow-hidden">
      <AuthShowcase />
      <div className="relative flex-1 lg:basis-1/2 lg:grow-0 lg:shrink-0 min-h-screen flex flex-col">
        <main className="flex-1 flex items-center justify-center px-4 py-8">
          <div className="w-full max-w-[400px]" data-testid="reset-password-page">

            {tokenError ? (
              <div className="text-center" data-testid="reset-token-error">
                <div className="w-14 h-14 mx-auto rounded-full bg-rose-50 flex items-center justify-center mb-4">
                  <XCircle size={26} weight="fill" className="text-rose-600" />
                </div>
                <h1 className="font-heading text-[22px] font-semibold text-slate-900">
                  Link inválido
                </h1>
                <p className="text-[13.5px] text-slate-500 mt-2">{tokenError}</p>
                <Link
                  to="/forgot-password"
                  data-testid="reset-request-new"
                  className="mt-5 inline-flex items-center gap-1.5 bg-slate-900 hover:bg-slate-700 text-white rounded-[10px] px-5 py-2.5 text-[13px] font-semibold"
                >
                  Solicitar novo link <ArrowRight size={12} weight="bold" />
                </Link>
              </div>
            ) : done ? (
              <div className="text-center" data-testid="reset-success">
                <div className="w-14 h-14 mx-auto rounded-full bg-emerald-50 flex items-center justify-center mb-4">
                  <CheckCircle size={26} weight="fill" className="text-emerald-600" />
                </div>
                <h1 className="font-heading text-[22px] font-semibold text-slate-900">
                  Senha redefinida com sucesso!
                </h1>
                <p className="text-[13.5px] text-slate-500 mt-2">
                  Você já pode entrar com sua nova senha. Redirecionando…
                </p>
              </div>
            ) : (
              <>
                <div className="text-center mb-6">
                  <div className="w-12 h-12 mx-auto rounded-full bg-slate-100 flex items-center justify-center mb-4">
                    <Lock size={20} weight="regular" className="text-slate-600" />
                  </div>
                  <h1 className="font-heading text-[24px] font-semibold text-slate-900 leading-tight">
                    Criar nova senha
                  </h1>
                  <p className="text-[13px] text-slate-500 mt-1.5">
                    Escolha uma senha forte que você consiga lembrar.
                  </p>
                </div>
                <form onSubmit={submit} data-testid="reset-form" noValidate className="bg-white border border-slate-200 rounded-[14px] px-6 pt-5 pb-5 shadow-[0_1px_0_rgba(15,23,42,0.03),0_8px_28px_-12px_rgba(15,23,42,0.07)]">
                  <div className="space-y-3">
                    <PwField
                      label="Nova senha" value={password} onChange={setPassword}
                      show={show} setShow={setShow} testid="reset-new-password"
                      hint="Mínimo de 8 caracteres."
                      error={touched.password ? passwordIssue : null}
                      onBlur={() => setTouched((t) => ({ ...t, password: true }))}
                    />
                    <PwField
                      label="Confirme a nova senha" value={confirm} onChange={setConfirm}
                      show={show} setShow={setShow} testid="reset-confirm-password"
                      error={touched.confirm ? confirmIssue : null}
                      onBlur={() => setTouched((t) => ({ ...t, confirm: true }))}
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="reset-submit-btn"
                    className="mt-5 w-full h-11 bg-slate-900 hover:bg-slate-700 disabled:opacity-60 text-white rounded-[10px] text-[13px] font-semibold inline-flex items-center justify-center gap-2 transition-colors"
                  >
                    {loading ? "Salvando…" : (<>Definir nova senha <ArrowRight size={13} weight="bold" /></>)}
                  </button>
                </form>
              </>
            )}
          </div>
        </main>
      </div>
      <Toaster position="top-right" richColors />
    </div>
  );
}

function PwField({ label, value, onChange, show, setShow, testid, hint, error, onBlur }) {
  const hasError = !!error;
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-[0.18em] text-slate-500 font-medium mb-1.5">{label}</span>
      <div className="relative">
        <input
          type={show ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          placeholder="••••••••"
          data-testid={testid}
          className={`w-full h-[42px] bg-[#FAFBFC] border rounded-[10px] pl-3 pr-10 text-[13.5px] outline-none focus:bg-white focus:ring-2 transition-colors ${hasError ? "border-rose-400 focus:border-rose-500 focus:ring-rose-500/15" : "border-[#E5E9EF] focus:border-slate-900 focus:ring-slate-900/5"}`}
        />
        <button type="button" onClick={() => setShow((v) => !v)} tabIndex={-1}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-900">
          {show ? <EyeSlash size={14} /> : <Eye size={14} />}
        </button>
      </div>
      <div className="mt-1.5 min-h-[16px] text-[11.5px]">
        {hasError ? (
          <span className="inline-flex items-center gap-1 text-rose-600" data-testid={`${testid}-error`}>
            <span className="w-1 h-1 rounded-full bg-rose-500" />
            {error}
          </span>
        ) : hint ? (
          <span className="text-slate-500">{hint}</span>
        ) : null}
      </div>
    </label>
  );
}
